import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import type { ErrorResponse } from "@core/types/errorResponse";
import { fetchPresentationLayout } from "@features/presentationLayout/api/presentationLayoutApi";
import LayoutSections from "@features/presentationLayout/components/LayoutSections";
import type { PresentationLayoutDetail, PresentationLayoutListItem } from "@features/presentationLayout/types/presentationLayout";
import { previewSectionsFromDetail } from "@features/presentationLayout/utils/layoutForm";

type Props = {
  show: boolean;
  onHide: () => void;
  nationId: number;
  departmentId: number;
  layout: PresentationLayoutListItem | null;
};

export default function PresentationLayoutPreviewModal({ show, onHide, nationId, departmentId, layout }: Props) {
  const [detail, setDetail] = useState<PresentationLayoutDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!show || !layout) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    fetchPresentationLayout(nationId, departmentId, layout.presentation_layout_id)
      .then((response) => {
        if (cancelled) return;
        if (response.isSuccess()) setDetail(response.getData() ?? null);
        else setError((response.getError() as ErrorResponse)?.detail || "Could not load the layout.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [show, layout, nationId, departmentId]);

  const sections = useMemo(() => (detail ? previewSectionsFromDetail(detail) : []), [detail]);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable data-testid="presentation-layout-preview-modal">
      <Modal.Header closeButton>
        <Modal.Title>Preview: {layout?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">Structure only. Values appear on department reports.</p>
        {loading ? (
          <div className="py-3">
            <Spinner animation="border" size="sm" className="me-2" aria-hidden />
            Loading…
          </div>
        ) : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {detail ? <LayoutSections sections={sections} /> : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
